import { useSelector } from "react-redux";
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from "@mui/x-data-grid-pro";

import { ActiveIndicator } from "../../../components/ActiveIndicator";
import { LoadingSkeleton } from "../../../components/LoadingSkeleton";
import { TableCellWithRightArrow } from "../../../components/TableCellWithRightArrow";
import { TableDateCell } from "../../../components/TableDateCell";
import { TablePaymentScheduleCell } from "../../../components/TablePaymentScheduleCell/TablePaymentScheduleCell";
import { TablePaymentStatusIndicatorCell } from "../../../components/TablePaymentStatusIndicatorCell";
import { TablePriceCell } from "../../../components/TablePriceCell";
import { TableWeightCell } from "../../../components/TableWeightCell";
import { getBillingBilling } from "../../../selectors/billing";
import { BillingLocationsTableType } from "../../../types/billingLocations";
import { IColumnAlignObject, IColumnValuePair } from "../../../types/table";
import { getTableColumnUtils } from "../../../utils/tables/getTableColumnUtils";
import { columnLabelsBilling, columnLabelsCalculation, ColumnType } from "./billingLocationsTableColumnTypes";
import { BillingLocationsTableHeaderCell } from "./BillingLocationsTableHeaderCell";

const columnAlignObject: IColumnAlignObject<ColumnType> = {
  center: [ColumnType.isActive],
};

const columnFlexValuePairs: IColumnValuePair<ColumnType>[] = [
  {
    name: ColumnType.name,
    value: 3,
  },
  {
    name: ColumnType.householdMembersCount,
    value: 2,
  },
  {
    name: ColumnType.enableTime,
    value: 2,
  },
  {
    name: ColumnType.previousWaste,
    value: 2,
  },
  {
    name: ColumnType.currentWaste,
    value: 3,
  },
  {
    name: ColumnType.amount,
    value: 2,
  },
  {
    name: ColumnType.realAmount,
    value: 2,
  },
  {
    name: ColumnType.netAmount,
    value: 2,
  },
  {
    name: ColumnType.balance,
    value: 2,
  },
  {
    name: ColumnType.isPaid,
    value: 2,
  },
  {
    name: ColumnType.paymentType,
    value: 2,
  },
];

const columnMinWidthValuePairs: IColumnValuePair<ColumnType>[] = [
  {
    name: ColumnType.isActive,
    value: 100,
  },
  {
    name: ColumnType.isPaid,
    value: 175,
  },
];

export const useGetBillingLocationsTableColumns = (tableType: BillingLocationsTableType) => {
  const { getFlex, getAlign, getWidth } = getTableColumnUtils();
  const billing = useSelector(getBillingBilling);

  const isCalculationTable = tableType === BillingLocationsTableType.Calculation;
  const columnLabels = isCalculationTable ? columnLabelsCalculation : columnLabelsBilling;

  const columns: GridColDef[] = columnLabels.map((label) => {
    const columnObject: GridColDef = {
      field: label,
      type: "string",
      renderHeader: (params: GridColumnHeaderParams) => {
        return <BillingLocationsTableHeaderCell params={params} />;
      },
      flex: getFlex(label, columnFlexValuePairs),
      align: getAlign(label, columnAlignObject),
      minWidth: getWidth(label, columnMinWidthValuePairs),
    };

    columnObject.renderCell = (params: GridRenderCellParams) => {
      if (params.row.isMocked) {
        return <LoadingSkeleton />;
      }

      switch (label) {
        case ColumnType.isActive:
          return (
            <ActiveIndicator
              selectedYear={billing.year}
              isActive={params.row.isActive}
              enableTime={params.row.enableTime}
              disableTime={params.row.disableTime}
            />
          );
        case ColumnType.enableTime:
          return <TableDateCell showTime={false} params={params} />;
        case ColumnType.amount:
          return <TablePriceCell params={params} percentChange={params.row.increaseComparison} reverseNegativeColors />;
        case ColumnType.netAmount:
          return <TablePriceCell params={params} reverseNegativeColors />;
        case ColumnType.realAmount:
        case ColumnType.balance:
          return <TablePriceCell params={params} reverseNegativeColors />;
        case ColumnType.previousWaste:
          return <TableWeightCell params={params} />;
        case ColumnType.currentWaste:
          return <TableWeightCell params={params} percentChange={params.row.wasteIncrease} reverseNegativeColors />;
        case ColumnType.paymentType:
          return <TableCellWithRightArrow mainComponent={<TablePaymentScheduleCell params={params} />} />;
        case ColumnType.isPaid:
          return (
            <TableCellWithRightArrow mainComponent={<TablePaymentStatusIndicatorCell params={params} withPaidText />} />
          );
        default:
          break;
      }
    };

    return columnObject;
  });

  return columns;
};
